import { InputHTMLAttributes, forwardRef } from 'react';

import { useFieldContext } from '@reckon-web/field';
import { Text } from '@reckon-web/text';
import { usePack } from '@reckon-web/theme';
import {
  Adornment,
  AdornmentWrapper,
  InputBaseProps,
  useInputStyles,
} from '@reckon-web/text-input';

import { useFormattedInput } from './utils';

export type CurrencyInputProps = {
  /** When true, a dollar symbol ($) will be displayed beside the input. */
  showAdornment?: boolean;
  onChange: (value: string | number) => void;
  value: string | number;
  // not including minimumFractionDigits because I haven't seen a use case yet
  // please raise it in #design-system if you think you need minimumFractionDigits
  // please also raise it in #design-system if you think there should be more valid values for maximumFractionDigits
  maximumFractionDigits?: 2 | 4;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size'> &
  Partial<Omit<InputBaseProps, 'shape'>>;

let validCurrencyInput = /^\s*\$?-?[\d,]+(\.\d+)?\s*$/;

function normaliseValue(value: string) {
  let normalisedValue = value.trim().replace(/^\$/, '').replace(/,/g, '');
  return normalisedValue;
}

function isValidCurrencyInput(value: string) {
  return (
    validCurrencyInput.test(value) && !isNaN(parseFloat(normaliseValue(value)))
  );
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      maximumFractionDigits = 2,
      showAdornment = true,
      size = 'medium',
      weight = 'subtle',
      ...consumerProps
    },
    ref
  ) => {
    const { invalid, ...a11yProps } = useFieldContext();

    if (Number.isNaN(consumerProps.value)) {
      throw new Error('NaN cannot be provided to CurrencyInput');
    }

    let formatter = Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      currencyDisplay: 'symbol',
      maximumFractionDigits,
      minimumFractionDigits: 2,
    });

    let format = (num: number) =>
      formatter
        .formatToParts(num)
        .filter((x) => x.type !== 'currency')
        .map((x) => x.value)
        .join('');

    const formattedProps = useFormattedInput(
      {
        determineType: (value: string | number) => {
          if (typeof value === 'string') {
            return 'raw';
          }
          return 'parsed';
        },
        format,
        parse: (value) => {
          // we're doing normalise -> parse -> format -> normalise -> parse here
          // because just doing normalise -> parse will mean that the parsed number will be more precise than the formatted input
          // i suppose we could change the regex to only allow 0-2 and 0-4 decimal places instead
          // slightly afraid that that could confuse users because they'd be like "wtf, this is valid. why are saying it isn't" for "1.00000" or "1.00001"
          // i suppose we could make the error messages more specific
          // that would mean you'd have to provide the maximumFractionDigits in two places
          // + we'd have to share logic from here to the forms package or the other way around.
          // the right thing is probably export form validate things + the scalar fields from the respective field packages
          // there's some naming bike shedding that you'd have to do though + what happens if something works for multiple fields and etc.
          let normalisedValue = normaliseValue(value);
          let formatted = format(parseFloat(normalisedValue));
          return parseFloat(normaliseValue(formatted));
        },
        validate: (value) => {
          return isValidCurrencyInput(value);
        },
      },
      consumerProps
    );

    const inputStyles = useInputStyles({
      shape: 'square',
      size,
      weight,
    });

    const sizePack = usePack('sizes')[size];
    const adornedStyles = showAdornment
      ? { paddingLeft: sizePack.paddingX * 2 }
      : { undefined };

    return (
      <AdornmentWrapper size={size} shape="square">
        {showAdornment && (
          <Adornment
            align="left"
            css={{
              fontSize: sizePack.fontSizeText, // FIXME: remove "size packs"
              pointerEvents: 'none',
            }}
          >
            <Text size={size}>$</Text>
          </Adornment>
        )}
        <input
          aria-invalid={invalid}
          css={{
            ...inputStyles,
            ...adornedStyles,
            fontVariantNumeric: 'tabular-nums', // ensure the numbers line up when stacked
          }}
          ref={ref}
          {...consumerProps}
          {...a11yProps}
          {...formattedProps}
        />
      </AdornmentWrapper>
    );
  }
);
